import {
    Autocomplete,
    AutocompleteOwnerState,
    AutocompleteProps,
    AutocompleteRenderGetTagProps,
    AutocompleteRenderOptionState,
    ChipProps,
    CircularProgress,
    TextField
} from "@mui/material";
import type {TextFieldProps} from "@mui/material/TextField";
import type {SxProps, Theme} from "@mui/material/styles";
import React, {HTMLAttributes, ReactNode} from "react";
import {useTranslation} from "react-i18next";

interface BaseProps<T> {
    label?: string;
    options: T[];
    value: T | T[] | null;
    getOptionLabel: (option: T) => string;
    error?: boolean;
    helperText?: string;
    loading?: boolean;
    noOptionsText?: string;
    onBlur?: React.FocusEventHandler;
    isOptionEqualToValue?: (option: T, value: T) => boolean;
    size?: "small" | "medium";
    fullWidth?: boolean;
    disableCloseOnSelect?: boolean;
    limitTags?: number;
    renderOption?: (props: HTMLAttributes<HTMLLIElement>, option: T, state: AutocompleteRenderOptionState) => ReactNode
    disabled?: boolean;
    open?: boolean,
    onOpen?: () => void,
    onClose?: () => void,
    inputValue?: string,
    onInputChange?: (value: string, reason: string) => void,
    filterOptions?: AutocompleteProps<T, boolean, false, false>['filterOptions'],
    renderTags?: (value: T[], getTagProps: AutocompleteRenderGetTagProps, ownerState: AutocompleteOwnerState<T, boolean, false, false, "div">) => ReactNode,
    ChipProps?: ChipProps<"div"> | undefined
    slotProps?: AutocompleteProps<T, boolean, false, false>['slotProps']
    textFieldProps?: TextFieldProps;
    sx?: SxProps<Theme>;
    freeSolo?: boolean;
}

interface SingleProps<T> extends BaseProps<T> {
    multiple?: false;
    onChange: (value: T | null) => void;
}

interface MultipleProps<T> extends BaseProps<T> {
    multiple: true;
    onChange: (value: T[] | null) => void;
}

type GenericAutocompleteProps<T> = SingleProps<T> | MultipleProps<T>;

export function GenericAutocomplete<T>(
    {
        label,
        options,
        value,
        getOptionLabel,
        onChange,
        error,
        helperText,
        loading = false,
        noOptionsText,
        onBlur,
        isOptionEqualToValue,
        size,
        fullWidth = false,
        multiple,
        open,
        onOpen,
        onClose,
        inputValue,
        onInputChange,
        filterOptions,
        renderTags,
        ChipProps,
        disableCloseOnSelect = false,
        limitTags,
        renderOption,
        disabled,
        slotProps,
        textFieldProps,
        sx,
        freeSolo = false,
    }: GenericAutocompleteProps<T>) {
    const {t} = useTranslation();
    return (
        <Autocomplete<T, boolean, false, boolean>
            size={size}
            fullWidth={fullWidth}
            options={options}
            value={value}
            inputValue={inputValue}
            freeSolo={freeSolo}
            getOptionLabel={getOptionLabel as (option: T | string) => string}
            onChange={(_, newValue) => {
                if (multiple) {
                    const multipleOnChange = onChange as MultipleProps<T>["onChange"];
                    multipleOnChange(newValue as T[] | null);
                    return;
                }

                const singleOnChange = onChange as SingleProps<T>["onChange"];
                singleOnChange(newValue as T | null);
            }}
            onInputChange={(_, newInputValue, reason) => onInputChange?.(newInputValue, reason)}
            noOptionsText={noOptionsText || t("No options")}
            loading={loading}
            loadingText={t("Loading...")}
            multiple={multiple}
            renderInput={(params) => (
                <TextField
                    {...params}
                    {...textFieldProps}
                    label={label}
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20}/> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            onBlur={onBlur}
            isOptionEqualToValue={isOptionEqualToValue}
            open={open}
            onOpen={onOpen}
            onClose={onClose}
            filterOptions={filterOptions}
            disableCloseOnSelect={disableCloseOnSelect}
            renderOption={renderOption}
            limitTags={limitTags}
            renderTags={renderTags as AutocompleteProps<T, boolean, false, boolean>['renderTags']}
            ChipProps={ChipProps}
            disabled={disabled}
            sx={sx}
            slotProps={slotProps}
        />
    );
}
